import { Link } from '@tanstack/react-router';
import {
  CALENDAR_JOIN_EARLY_MS,
  CALENDAR_SERIES_SCOPE_LABEL,
  calendarMessageMetaSchema,
  type CalendarMessageMeta,
  type Message,
} from '@avslack/shared';
import { useCalendarEvent } from '../data/calendar';
import { channelTitle, useChannels } from '../data/channels';
import { useGroups } from '../data/workspace';
import { useUsers } from '../data/users';
import { formatTime } from '../lib/format';
import { openHuddleWindow } from '../lib/huddle-window';
import { useSession } from '../lib/session';
import { AttendeeSummary, ResponseButtons } from './CalendarMeeting';
import { Icon } from './Icon';

/**
 * A "NAPTAR" UZENETE (docs/23): meghivo, modositas, lemondas, vagy a szervezonek
 * a valaszok osszesitese.
 *
 * A kartya a kuldeskori PILLANATKEPBOL rajzol (`meta`), igy egy azota
 * modositott vagy elmaradt megbeszeles uzenete is olvashato marad. A valasz-
 * gombok es a resztvevok allasa viszont ELO: a bejegyzest kerdezi le.
 */

const KIND_LABEL: Record<CalendarMessageMeta['kind'], string> = {
  invite: 'Meghívó',
  update: 'Módosult',
  cancel: 'Elmarad',
  responses: 'Válaszok a meghívódra',
};

const dateText = (iso: string) =>
  new Date(iso).toLocaleDateString('hu-HU', { weekday: 'long', month: 'long', day: 'numeric' });
const timeText = (iso: string) =>
  new Date(iso).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });

function whenOf(meta: Pick<CalendarMessageMeta, 'start' | 'end' | 'allDay'>): string {
  return meta.allDay
    ? `${dateText(meta.start)} · egész nap`
    : `${dateText(meta.start)} · ${timeText(meta.start)}–${timeText(meta.end)}`;
}

export function CalendarMessageCard({ message }: { message: Message }) {
  const parsed = calendarMessageMetaSchema.safeParse(message.meta);
  const me = useSession().data?.user;
  const { data: channels } = useChannels();
  const { data: groups } = useGroups();
  const { usersById, nameDisplay, nameOf } = useUsers();
  const live = parsed.success && parsed.data.kind !== 'cancel';
  const { data: event, isError } = useCalendarEvent(live ? parsed.data.eventId : null);

  if (!parsed.success) {
    return <div className="px-3.5 py-1.5 text-xs text-ink-faint">{message.text}</div>;
  }
  const meta = parsed.data;
  const canceled = meta.kind === 'cancel';
  const isOrganizer = meta.organizerId === me?.id;
  const channel = channels?.find((c) => c.id === meta.channelId);
  const source =
    meta.scope === 'channel'
      ? channel
        ? `#${channelTitle(channel, usersById, me?.id ?? '', nameDisplay)}`
        : 'csatorna'
      : `@${groups?.find((g) => g.id === meta.groupId)?.handle ?? 'csoport'}`;
  const now = Date.now();
  const joinable =
    !canceled &&
    meta.withCall &&
    meta.channelId &&
    now >= new Date(meta.start).getTime() - CALENDAR_JOIN_EARLY_MS &&
    now <= new Date(meta.end).getTime();

  return (
    <div className="px-3.5 py-2">
      <section
        aria-label={`Naptár: ${KIND_LABEL[meta.kind]}`}
        className={[
          'max-w-md rounded-card border bg-surface-raised px-4 py-3 shadow-(--shadow-soft)',
          canceled ? 'border-[var(--danger)]/40' : 'border-border-soft',
        ].join(' ')}
      >
        <p className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
          <span aria-hidden className={canceled ? 'text-[var(--danger)]' : 'text-accent'}>
            <Icon name="naptar" size="sm" />
          </span>
          {KIND_LABEL[meta.kind]}
          {meta.seriesScope && meta.seriesScope !== 'all' && meta.kind !== 'invite' && (
            <span className="font-normal normal-case">
              · {CALENDAR_SERIES_SCOPE_LABEL[meta.seriesScope].toLowerCase()}
            </span>
          )}
          <time className="ml-auto font-normal normal-case" dateTime={message.createdAt}>
            {formatTime(message.createdAt)}
          </time>
        </p>

        <p
          className={`mt-1 text-sm font-semibold text-ink ${canceled ? 'line-through opacity-70' : ''}`}
        >
          {meta.withCall && <span aria-hidden>📞 </span>}
          {meta.title}
        </p>
        <p className="mt-0.5 text-xs text-ink-muted first-letter:uppercase">
          {whenOf(meta)}
          {meta.repeatText ? ` · ${meta.repeatText}` : ''}
        </p>
        {meta.previousStart && (
          <p className="text-xs text-ink-faint">
            Korábban: <span className="line-through">{dateText(meta.previousStart)}</span>{' '}
            {!meta.allDay && <span className="line-through">{timeText(meta.previousStart)}</span>}
          </p>
        )}
        <p className="mt-1 text-xs text-ink-faint">
          {source}
          {meta.organizerId && !isOrganizer ? ` · szervező: ${nameOf(meta.organizerId)}` : ''}
        </p>

        {live && event && !isOrganizer && meta.kind !== 'responses' && (
          <div className="mt-3">
            <ResponseButtons eventId={meta.eventId} current={event.myResponse} size="sm" />
          </div>
        )}
        {live && event && (isOrganizer || meta.kind === 'responses') && (
          <div className="mt-3">
            <AttendeeSummary event={event} />
          </div>
        )}
        {live && isError && (
          <p className="mt-2 text-xs text-ink-faint">Ez a megbeszélés azóta elmaradt.</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {joinable && (
            <button
              type="button"
              onClick={() => void openHuddleWindow(meta.channelId!)}
              className="rounded-control bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink"
            >
              <Icon name="huddle" size="sm" /> Csatlakozás
            </button>
          )}
          <Link to="/naptar" className="text-xs font-semibold text-accent hover:underline">
            Megnyitás a naptárban
          </Link>
        </div>
      </section>
    </div>
  );
}
